import React, { useState } from "react";
import { FaCheck, FaStar, FaUsers, FaHeart } from "react-icons/fa";
import Select from "react-select";

const SubscriptionSection = () => {
  const [selectedType, setSelectedType] = useState({
    value: "UnionAndAffiliateMembers",
    label: "Union & Affiliate Members",
  });

  // State for global toggle (Monthly/Yearly) for all plans
  const [isYearly, setIsYearly] = useState(false);

  // Subscription plans data for each user type (updated to match client's new pricing plans)
  const subscriptionPlans = {
    UnionAndAffiliateMembers: {
      // Combined Union Members, Unions, and Affiliate Members into one type
      basic: {
        // Renamed from "free" to "basic" for consistency
        title: "Basic",
        price: "$0", // Will show as "Free"
        monthlyPrice: "$0",
        yearlyPrice: "$0",
        period: "per month",
        icon: FaUsers,
        features: [
          "Basic profile",
          "Limited job visibility",
          "Networking",
          "Messaging",
          "Search capabilities",
          "Workshops & Classes",
          "Join Groups & Discussions",
          "Event listings",
          "+ More!"
        ],
      },
      pro: {
        // New Pro plan for Union & Affiliate Members
        title: "Pro",
        monthlyPrice: "$7.99", // Monthly price
        yearlyPrice: "$83.99", // Yearly price
        period: "/mo or $160/yr",
        icon: FaStar,
        features: [
          "Job Listings",
          "Auditions",
          "Agent and Manager discovery",
          "Creative Play (with peers) posts",
          "Additional Featured listings",
          "Priority Search",
          "Event promotions",
          "Increased visibility",
          "+ More!"
        ],
      },
    },
    NonUnionIndividuals: {
      // Renamed from "Non-Union individuals / Hobbyists"
      basic: {
        // Renamed from "free" to "basic" for consistency
        title: "Basic",
        price: "$0", // Will show as "Free"
        monthlyPrice: "$0",
        yearlyPrice: "$0",
        period: "per month",
        icon: FaUsers,
        features: [
          "Basic profile",
          "Limited job visibility",
          "Networking",
          "Messaging",
          "Search capabilities",
          "Workshops & Classes",
          "Join Groups & Discussions",
          "Event listings",
          "+ More!"
        ],
      },
      premium: {
        // Renamed from "gold" to "premium" for consistency
        title: "Premium",
        monthlyPrice: "$9.99", // Monthly price
        yearlyPrice: "$106.99", // Yearly price
        period: "/mo or $106.99/yr",
        icon: FaStar,
        features: [
            "Job Listings",
          "Auditions",
          "Agent and Manager discovery",
          "Creative Play (with peers) posts",
          "Additional Featured listings",
          "Priority Search",
          "Event promotions",
          "Increased visibility",
          "+ More!"
        ],
      },
    },
    AudienceMembersFans: {
      // Renamed from "Audience Members / Fans"
      basic: {
        // Renamed from "free" to "basic" for consistency
        title: "Basic",
        price: "$0", // Will show as "Free"
        monthlyPrice: "$0",
        yearlyPrice: "$0",
        // period: "per month",
        icon: FaHeart,
        features: [
          "Connect with the work of Artists, Performers, Entertainers, Creative Professionals & more!",
          "Have your page filled with art & creative expressions that you love",
          "Connect with others who have the same interests as you",
          "Join Groups around your interests",
          "Engage in Discussions",
          "Stay updated on General Public Events involving your favorite artists, performers & creative professionals",
          "Repost & share what inspires / your favorite posts",
        ],
      },
    },
  };

  const options = Object.keys(subscriptionPlans).map((type) => ({
    value: type,
    label:
      type === "UnionAndAffiliateMembers"
        ? "Union & Affiliate Members"
        : type === "NonUnionIndividuals"
        ? "Non-Union Individuals"
        : "Audience Members / Fans",
  }));

  const customStyles = {
    control: (base, state) => ({
      ...base,
      background: "white",
      color: "black",
      padding: "5px",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(48, 213, 200, 0.5)" : "none",
      borderColor: "#30D5C8",
      "&:hover": { borderColor: "#30D5C8" },
      borderRadius: "25px",
      width: "100%", // Full width
      minWidth: "200px", // Prevent collapsing
      maxWidth: "350px",
      minHeight: "45px",
      textAlign: "center",
    }),
    menu: (base) => ({
      ...base,
      background: "white",
      borderRadius: "8px",
      boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
      width: "100%", // Full width
      minWidth: "200px", // Prevent collapsing
      maxWidth: "350px",
    }),
    option: (base, { isFocused }) => ({
      ...base,
      backgroundColor: isFocused ? "#30D5C8" : "white",
      "&:hover": { backgroundColor: "#30D5C8" },
      color: isFocused ? "white" : "black",
      cursor: "pointer",
      padding: "10px",
    }),
  };

  // Get the selected plans
  const plans = subscriptionPlans[selectedType.value];

  const ToggleButton = () => (
    <div className="mb-6 flex justify-center">
      <button
        onClick={() => setIsYearly(false)}
        className={`px-4 py-2 rounded-l-full text-sm font-medium ${
          !isYearly
            ? "bg-white text-black border border-[#30D5C8]"
            : "bg-gray-200 text-gray-600"
        }`}
      >
        Monthly
      </button>
      <button
        onClick={() => setIsYearly(true)}
        className={`px-4 py-2 rounded-r-full text-sm font-medium ${
          isYearly ? "bg-[#30D5C8] text-white" : "bg-gray-200 text-gray-600"
        }`}
      >
        Yearly
      </button>
    </div>
  );

  return (
    <section id="pricing" className="py-20 bg-gray-50 ">
      <div className="container mx-auto text-center">
        <h2 className="text-4xl font-bold text-[#30D5C8] mb-6">
          Pricing Plans
        </h2>
        <p className="text-xl text-gray-600 mb-12 px-4 lg:px-0">
          Choose the plan that fits your needs. Save with annual subscriptions!
        </p>

        <div className="mb-8 flex justify-center px-4 md:px-4 lg:px-0 w-full">
          <div className="w-full max-w-[380px]">
            <Select
              value={selectedType}
              onChange={setSelectedType}
              options={options}
              styles={customStyles}
              isSearchable={false}
            />
          </div>
        </div>

        {/* Toggle Button for Monthly/Yearly */}
        <ToggleButton />

        {/* Subscription Cards for Selected Type (Basic and Pro/Premium in one row on md+, stack on sm-) */}
        <div className="flex flex-col sm:flex-row justify-center gap-8 px-4">
          {/* Basic Plan Card */}
          <div className="p-8 bg-white shadow-lg rounded-lg flex flex-col items-center w-full sm:w-[40%]">
            {React.createElement(plans.basic.icon, {
              className: "text-4xl text-[#30D5C8] mb-4",
            })}
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
              {plans.basic.title}
            </h3>
            <div className="mb-1 flex items-start justify-center">
              <span className="text-2xl lg:text-4xl font-bold">
                {plans.basic.price === "$0" ? "Free" : plans.basic.monthlyPrice}
              </span>
              <span className="text-gray-600 text-sm ml-2">
                {plans.basic.price === "$0" ? plans.basic.period : "per month"}
              </span>
            </div>
            {plans.basic.price === "$0" && (
              <p className="text-[#30D5C8] font-semibold text-sm mb-1 text-center">Free Forever</p>
            )}
            <ul className="text-gray-600 space-y-3 text-left w-full mt-6">
              {plans.basic.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <FaCheck className="w-5" style={{ color: "#25c7ba" }} /> {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro Plan Card for Union & Affiliate Members */}
          {plans.pro && (
            <div className="p-8 bg-white shadow-lg rounded-lg flex flex-col items-center w-full sm:w-[40%]">
              {React.createElement(plans.pro.icon, {
                className: "text-4xl text-[#30D5C8] mb-4",
              })}
              <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                {plans.pro.title}
              </h3>
              <div className="mb-1 flex items-start justify-center">
                <span className="text-2xl lg:text-4xl font-bold">
                  {plans.pro.price === "$0"
                    ? "Free"
                    : isYearly
                    ? plans.pro.yearlyPrice
                    : plans.pro.monthlyPrice}
                </span>
                <span className="text-gray-600 text-sm ml-2">
                  {plans.pro.price === "$0"
                    ? plans.pro.period
                    : isYearly
                    ? "per year"
                    : "per month"}
                </span>
              </div>
              {plans.pro.price !== "$0" && (
                <p className="text-[#30D5C8] font-semibold text-sm mb-1 text-center">Starting December 1st</p>
              )}
              <p className="text-gray-500 text-sm text-left w-full mt-6 mb-2 italic">Includes everything in Basic plus:</p>
              <ul className="text-gray-600 space-y-3 text-left w-full">
                {plans.pro.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <FaCheck className="w-5" style={{ color: "#25c7ba" }} /> {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Premium Plan for Non-Union Individuals */}
          {plans.premium && (
            <div className="p-8 bg-white shadow-lg rounded-lg flex flex-col items-center w-full sm:w-[40%]">
              {React.createElement(plans.premium.icon, {
                className: "text-4xl text-[#30D5C8] mb-4",
              })}
              <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                {plans.premium.title}
              </h3>
              <div className="mb-1 flex items-start justify-center">
                <span className="text-2xl lg:text-4xl font-bold">
                  {plans.premium.price === "$0"
                    ? "Free"
                    : isYearly
                    ? plans.premium.yearlyPrice
                    : plans.premium.monthlyPrice}
                </span>
                <span className="text-gray-600 text-sm ml-2">
                  {plans.premium.price === "$0"
                    ? plans.premium.period
                    : isYearly
                    ? "per year"
                    : "per month"}
                </span>
              </div>
              {plans.premium.price !== "$0" && (
                <p className="text-[#30D5C8] font-semibold text-sm mb-1 text-center">Starting December 1st</p>
              )}
              <p className="text-gray-500 text-sm text-left w-full mt-6 mb-2 italic">Includes everything in Basic plus:</p>
              <ul className="text-gray-600 space-y-3 text-left w-full">
                {plans.premium.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <FaCheck className="w-5" style={{ color: "#25c7ba" }} /> {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SubscriptionSection;
